/**
 * IdeaCraftingModal — UI surface for the Descriptor agent.
 *
 * Item Detail agent for writing / improving title and description given
 * whichever side the user already has. Flow:
 *   1. Run craftIdea on the current title + description.
 *   2. Review suggested title and description (editable before apply).
 *   3. Apply both, title only, description only, or discard.
 *
 * Uses idea_crafting (callFeatureLlm). Pattern B modal (Headless UI Dialog).
 */

import { Fragment, useState, useCallback, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, Loader2, AlertCircle, CheckCircle2, RotateCcw, Sparkles } from 'lucide-react';
import { AgentGlyph } from './AgentGlyph';
import { craftIdea, type CraftIdeaResult } from '../../lib/llmIdeaCraftingService';

export interface IdeaCraftingModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  onApply: (next: { title: string; description: string }) => void | Promise<void>;
}

type Phase = 'ready' | 'running' | 'review';

export function IdeaCraftingModal({
  open,
  onClose,
  title,
  description,
  onApply,
}: IdeaCraftingModalProps) {
  const [phase, setPhase] = useState<Phase>('ready');
  const [error, setError] = useState<string | null>(null);
  const [proposal, setProposal] = useState<CraftIdeaResult | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [applying, setApplying] = useState(false);

  // Reset when reopened so each visit starts clean.
  useEffect(() => {
    if (!open) return;
    setPhase('ready');
    setError(null);
    setProposal(null);
    setEditTitle('');
    setEditDescription('');
    setApplying(false);
  }, [open]);

  const hasSeed = Boolean(title.trim() || description.trim());
  const missingSide = !title.trim()
    ? 'description → title'
    : !description.trim()
      ? 'title → description'
      : 'improve both';

  const handleRun = useCallback(async () => {
    if (!hasSeed) {
      setError('Add a title or a description first — the agent needs one side to write the other.');
      return;
    }
    setError(null);
    setPhase('running');
    try {
      const result = await craftIdea({
        title: title.trim(),
        description: description.trim(),
      });
      setProposal(result);
      setEditTitle(result.suggestedTitle);
      setEditDescription(result.suggestedDescription);
      setPhase('review');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Descriptor failed');
      setPhase('ready');
    }
  }, [hasSeed, title, description]);

  const handleApply = useCallback(
    async (mode: 'both' | 'title' | 'description') => {
      setApplying(true);
      setError(null);
      try {
        const nextTitle = mode === 'description' ? title : editTitle.trim();
        const nextDescription = mode === 'title' ? description : editDescription.trim();
        if (mode !== 'description' && !nextTitle) {
          throw new Error('Suggested title is empty — edit it or apply description only.');
        }
        await onApply({ title: nextTitle, description: nextDescription });
        onClose();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not apply suggestions');
        setApplying(false);
      }
    },
    [editTitle, editDescription, title, description, onApply, onClose],
  );

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 translate-y-2 scale-95"
            enterTo="opacity-100 translate-y-0 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0 scale-100"
            leaveTo="opacity-0 translate-y-2 scale-95"
          >
            <Dialog.Panel className="relative w-full max-w-lg bg-[var(--bg-raised)] dark:bg-[var(--surface)] shadow-2xl border border-[var(--rule)]/80 dark:border-[var(--rule)] overflow-hidden">
              <div className="px-5 pt-5 pb-4 bg-gradient-to-br from-indigo-500/10 to-violet-500/5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <AgentGlyph
                      type="idea-crafting"
                      colorScheme="indigo"
                      size={44}
                      pulse={phase !== 'running'}
                    />
                    <div>
                      <Dialog.Title className="text-base font-semibold text-[var(--text-pri)] dark:text-charcoal-50 leading-tight">
                        Descriptor
                      </Dialog.Title>
                      <p className="text-xs text-[var(--text-ter)] dark:text-charcoal-400 mt-0.5 leading-snug">
                        Write title or description from the other — or sharpen both
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-shrink-0 p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--bg-raised)]/60 dark:hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="px-5 py-4 space-y-4">
                {phase === 'ready' && (
                  <>
                    <p className="text-sm text-[var(--text-sec)] dark:text-charcoal-300 leading-relaxed">
                      Given whichever side you already have, this agent drafts the missing
                      side — or rewrites both for clarity. Nothing is saved until you apply.
                    </p>
                    <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)] px-3 py-2.5 space-y-1.5">
                      <p className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wider">
                        Current seed
                      </p>
                      <p className="text-xs text-[var(--text-pri)] dark:text-charcoal-200">
                        <span className="font-medium">Title:</span>{' '}
                        {title.trim() || <span className="text-[var(--text-ter)] italic">(empty)</span>}
                      </p>
                      <p className="text-xs text-[var(--text-pri)] dark:text-charcoal-200">
                        <span className="font-medium">Description:</span>{' '}
                        {description.trim()
                          ? description.trim().length > 160
                            ? `${description.trim().slice(0, 160)}…`
                            : description.trim()
                          : <span className="text-[var(--text-ter)] italic">(empty)</span>}
                      </p>
                      <p className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-ter)] pt-1">
                        Will run as: {missingSide}
                      </p>
                    </div>
                  </>
                )}

                {phase === 'running' && (
                  <div className="flex flex-col items-center justify-center py-8 gap-3 text-[var(--text-ter)]">
                    <Loader2 className="h-6 w-6 animate-spin text-[var(--text-ter)]" />
                    <p className="text-sm">Crafting suggestions…</p>
                  </div>
                )}

                {phase === 'review' && proposal && (
                  <div className="space-y-3">
                    {proposal.reasoning && (
                      <p className="text-xs text-[var(--text-ter)] dark:text-charcoal-400 leading-relaxed">
                        {proposal.reasoning}
                      </p>
                    )}
                    <label className="block space-y-1">
                      <span className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wider">
                        Suggested title
                      </span>
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        className="w-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] px-3 py-2 text-sm text-[var(--text-pri)] dark:text-charcoal-50 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                      />
                    </label>
                    <label className="block space-y-1">
                      <span className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wider">
                        Suggested description
                      </span>
                      <textarea
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        rows={5}
                        className="w-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] px-3 py-2 text-sm text-[var(--text-pri)] dark:text-charcoal-50 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 resize-y"
                      />
                    </label>
                    {proposal.tips && proposal.tips.length > 0 && (
                      <ul className="space-y-1">
                        {proposal.tips.map((tip) => (
                          <li key={tip} className="text-xs text-[var(--text-ter)] flex gap-2">
                            <Sparkles className="h-3 w-3 flex-shrink-0 mt-0.5 text-[var(--text-ter)]" />
                            {tip}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {error && (
                  <div className="flex items-start gap-2 text-xs text-[var(--coral)] bg-[var(--coral-dim)] dark:bg-[var(--coral)]/30 border border-[var(--coral)] dark:border-[var(--coral)]/50 px-3 py-2">
                    <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    {error}
                  </div>
                )}
              </div>

              <div className="px-5 pb-5 flex flex-wrap items-center justify-end gap-2">
                {phase === 'ready' && (
                  <>
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-3 py-1.5 text-sm text-[var(--text-sec)] dark:text-charcoal-300 hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleRun}
                      disabled={!hasSeed}
                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      Run agent
                    </button>
                  </>
                )}

                {phase === 'review' && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setPhase('ready');
                        setProposal(null);
                        setError(null);
                      }}
                      disabled={applying}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--text-sec)] dark:text-charcoal-300 hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Try again
                    </button>
                    <button
                      type="button"
                      onClick={() => handleApply('title')}
                      disabled={applying || !editTitle.trim()}
                      className="px-3 py-1.5 text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] border border-[var(--slate)] dark:border-[var(--slate)] hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/40 disabled:opacity-50 transition-colors"
                    >
                      Title only
                    </button>
                    <button
                      type="button"
                      onClick={() => handleApply('description')}
                      disabled={applying || !editDescription.trim()}
                      className="px-3 py-1.5 text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] border border-[var(--slate)] dark:border-[var(--slate)] hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/40 disabled:opacity-50 transition-colors"
                    >
                      Description only
                    </button>
                    <button
                      type="button"
                      onClick={() => handleApply('both')}
                      disabled={applying || !editTitle.trim()}
                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 transition-colors"
                    >
                      {applying ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      )}
                      Apply both
                    </button>
                  </>
                )}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
